import { cva, type VariantProps } from 'class-variance-authority'
import React from 'react'

import { cn } from '../utils'

const buttonQrTypeVariants = cva(
	'flex cursor-pointer items-center transition [&>svg]:transition',
	{
		variants: {
			visual: {
				outlined:
					'bg-white text-black ring-inset ring-border-grey hover:shadow-input hover:ring-blue',
				filled: 'shadow-generator ring-transparent hover:ring-blue',
			},
			size: {
				default: 'gap-2 rounded-full p-2 pr-3 ring-1',
				large: 'gap-6 rounded-10 p-4 ring-2',
			},
		},
		defaultVariants: {
			visual: 'outlined',
			size: 'default',
		},
	},
)

type ButtonQrTypeVisualVariant = VariantProps<
	typeof buttonQrTypeVariants
>['visual']
type ButtonQrTypeSizeVariant = VariantProps<typeof buttonQrTypeVariants>['size']

const ButtonQrTypeIcon = ({
	icon,
	size,
	isActive,
}: {
	icon: React.ReactElement<SVGSVGElement>
	size: string
	isActive?: boolean
}) => {
	const iconSize = size === 'large' ? '1.625rem' : '1.5rem'

	return (
		<div
			className={cn(
				'flex shrink-0 items-center justify-center bg-purple-8 p-2 transition-colors group-hover:bg-blue',
				isActive && 'bg-blue text-white',
				size === 'large' ? 'rounded-10' : 'rounded-full',
			)}
			aria-hidden="true"
		>
			{icon && (
				<span
					className={cn(
						isActive ? 'text-white' : 'text-blue',
						'flex items-center justify-center group-hover:text-white',
					)}
					style={{ width: iconSize, height: iconSize }}
					role="img"
				>
					{icon}
				</span>
			)}
		</div>
	)
}

const ButtonQrTypeContent = ({
	title,
	subtitle,
	size,
}: {
	title: string
	subtitle?: string
	size: string
}) => (
	<div className="flex flex-col items-start overflow-hidden text-left">
		<span
			className={cn(
				'line-clamp-2',
				size === 'large' ? 'type-heading-4' : 'type-body-small',
			)}
		>
			{title}
		</span>
		{size === 'large' && subtitle ? (
			<span className="type-body-small line-clamp-2 text-dark-grey">
				{subtitle}
			</span>
		) : null}
	</div>
)

interface ButtonQrTypeProps
	extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'asChild'>,
		VariantProps<typeof buttonQrTypeVariants> {
	icon?: React.ReactElement<SVGSVGElement>
	title: string
	subtitle?: string
	isActive?: boolean
}

const ButtonQrType = React.forwardRef<HTMLButtonElement, ButtonQrTypeProps>(
	(
		{
			className,
			visual,
			size = 'default',
			icon,
			title,
			subtitle,
			isActive,
			...props
		},
		ref,
	) => (
		<button
			className={cn(
				buttonQrTypeVariants({ visual, size, className }),
				`${isActive ? 'bg-white shadow-input ring-blue' : null} group`,
			)}
			aria-label={title}
			aria-describedby={subtitle ? 'subtitle-id' : undefined}
			ref={ref}
			{...props}
		>
			{icon && (
				<ButtonQrTypeIcon
					icon={icon}
					size={size ? size : ''}
					isActive={isActive}
				/>
			)}
			<ButtonQrTypeContent
				title={title}
				subtitle={subtitle}
				size={size ? size : ''}
			/>
		</button>
	),
)

ButtonQrType.displayName = 'ButtonQrType'

export {
	ButtonQrType,
	type ButtonQrTypeProps,
	type ButtonQrTypeSizeVariant,
	buttonQrTypeVariants,
	type ButtonQrTypeVisualVariant,
}
